import type { Color, ToastButton } from "@ionic/core";
import type { UseIonToastResult } from "@ionic/react";
import { Cubit } from "blac";
import translate from "src/lib/translate";
import type { TranslationKey } from "src/types/translationKey";
import type { ReactElement } from "react";
import { createRoot } from "react-dom/client";

type ToastState = {
  linked: boolean;
};

export default class ToastBloc extends Cubit<ToastState> {
  ionicToast?: UseIonToastResult;

  constructor() {
    super({
      linked: false
    });
  }

  linkToast = (ionicToast: UseIonToastResult | null) => {
    if (this.ionicToast !== undefined || ionicToast === null) {
      return;
    }

    this.ionicToast = ionicToast;
    this.emit({ ...this.state, linked: true });
  };

  show = (
    message: TranslationKey,
    options: {
      severity?: Color;
      duration?: number;
      translationVariables?: Record<string, unknown>;
      buttons?: (ToastButton | string)[];
      onDidDismiss?: () => void;
      icon?: ReactElement;
      cssClass?: string;
    } = {}
  ) => {
    if (!this.ionicToast) {
      return;
    }
    const { severity, duration = 7000, icon } = options;

    const position = window.innerWidth < 768 ? "bottom" : "top";

    const msg = translate(message, options.translationVariables);

    const [present] = this.ionicToast;

    let cssClasses = options.cssClass ?? "";
    if (options.buttons && options.buttons.length > 0) {
      cssClasses += " toast-with-buttons";
    }
    if (icon) {
      cssClasses += " toast-with-icon";
    }

    void present({
      message: msg,
      duration,
      color: severity,
      position,
      buttons: options.buttons,
      positionAnchor: "bottom",
      onDidDismiss: options.onDidDismiss,
      keyboardClose: true,
      swipeGesture: "vertical",
      cssClass: cssClasses.trim()
    }).then(() => {
      requestAnimationFrame(() => {
        const toastEl = document.querySelector("ion-toast.ion-color-success");
        if (toastEl?.shadowRoot) {
          if ((options.buttons && options.buttons.length > 0) || icon) {
            const toastContent = toastEl.shadowRoot.querySelector(
              ".toast-content"
            ) as HTMLElement;

            toastContent.style.flex = "unset";
          }

          if (icon) {
            const container =
              toastEl.shadowRoot.querySelector(".toast-container");
            if (container) {
              const iconContainer = document.createElement("div");
              container.insertBefore(iconContainer, container.firstChild);
              const root = createRoot(iconContainer);
              root.render(icon);
            }
          }
        }
      });
    });
  };

  error = (
    message: TranslationKey,
    translationVariables?: Record<string, unknown>
  ) => {
    this.show(message, { severity: "danger", translationVariables });
  };

  success = (message: TranslationKey, icon?: ReactElement) => {
    this.show(message, { severity: "success", icon });
  };
}
